import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';


const ViewStudent = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchStudent = async () => {
      try {
        // In a real app, this would be an API call
        // const response = await axios.get(`/api/students/${id}`);
        
        // Mock data for now
        const mockStudent = {
          _id: id,
          userId: {
            _id: 'user_' + id,
            name: 'John Smith',
            email: 'john.smith@example.com',
            phone: '+1234567890',
            address: '123 Main St, City, Country',
            avatar: '',
            role: 'student',
            isActive: true
          },
          roll: 'STU001',
          class: '10-A',
          section: 'A',
          session: '2024-2025',
          dateOfBirth: '2008-05-15',
          bloodGroup: 'O+',
          guardianName: 'Robert Smith',
          guardianPhone: '+1987654321',
          admissionDate: '2024-01-15',
          createdAt: '2024-01-15T10:30:00Z',
          updatedAt: '2024-01-15T10:30:00Z'
        };

        setStudent(mockStudent);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching student:', error);
        setError('Failed to load student data');
        setLoading(false);
      }
    };

    fetchStudent();
  }, [id]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl text-red-600">{error}</div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl text-red-600">Student not found</div>
      </div>
    );
  }

  const { userId, roll, class: studentClass, section, session, dateOfBirth, bloodGroup, guardianName, guardianPhone, admissionDate } = student;

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">View Student</h1>
          <Link 
            to="/admin/students" 
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition"
          >
            Back to Students
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Student Header */}
          <div className="bg-gradient-to-r from-primary to-blue-700 text-white p-6">
            <div className="flex items-center">
              <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16 flex items-center justify-center">
                <span className="text-gray-500 text-2xl">👤</span>
              </div>
              <div className="ml-4">
                <h2 className="text-2xl font-bold">{userId.name}</h2>
                <p className="text-blue-100">Roll: {roll} | Class: {studentClass}-{section}</p>
              </div>
            </div>
          </div>

          {/* Student Details */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Personal Information */}
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b">Personal Information</h3>
                
                <div className="space-y-4">
                  <div className="flex">
                    <span className="font-medium text-gray-600 w-40">Full Name:</span>
                    <span className="text-gray-800">{userId.name}</span>
                  </div>
                  
                  <div className="flex">
                    <span className="font-medium text-gray-600 w-40">Email:</span>
                    <span className="text-gray-800">{userId.email}</span>
                  </div>
                  
                  <div className="flex">
                    <span className="font-medium text-gray-600 w-40">Phone:</span>
                    <span className="text-gray-800">{userId.phone}</span>
                  </div>
                  
                  <div className="flex">
                    <span className="font-medium text-gray-600 w-40">Address:</span>
                    <span className="text-gray-800">{userId.address}</span>
                  </div>
                  
                  <div className="flex">
                    <span className="font-medium text-gray-600 w-40">Date of Birth:</span>
                    <span className="text-gray-800">{formatDate(dateOfBirth)}</span>
                  </div>
                  
                  <div className="flex">
                    <span className="font-medium text-gray-600 w-40">Blood Group:</span>
                    <span className="text-gray-800">{bloodGroup}</span>
                  </div>
                </div>
              </div>

              {/* Academic Information */}
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b">Academic Information</h3>
                
                <div className="space-y-4">
                  <div className="flex">
                    <span className="font-medium text-gray-600 w-40">Roll Number:</span>
                    <span className="text-gray-800">{roll}</span>
                  </div>
                  
                  <div className="flex">
                    <span className="font-medium text-gray-600 w-40">Class:</span>
                    <span className="text-gray-800">{studentClass}</span>
                  </div>
                  
                  <div className="flex">
                    <span className="font-medium text-gray-600 w-40">Section:</span>
                    <span className="text-gray-800">{section}</span>
                  </div>
                  
                  <div className="flex">
                    <span className="font-medium text-gray-600 w-40">Session:</span>
                    <span className="text-gray-800">{session}</span>
                  </div>
                  
                  <div className="flex">
                    <span className="font-medium text-gray-600 w-40">Admission Date:</span>
                    <span className="text-gray-800">{formatDate(admissionDate)}</span>
                  </div>
                </div>
              </div>

              {/* Guardian Information */}
              <div className="md:col-span-2">
                <h3 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b">Guardian Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex">
                    <span className="font-medium text-gray-600 w-40">Guardian Name:</span>
                    <span className="text-gray-800">{guardianName}</span>
                  </div>
                  
                  <div className="flex">
                    <span className="font-medium text-gray-600 w-40">Guardian Phone:</span>
                    <span className="text-gray-800">{guardianPhone}</span>
                  </div>
                </div>
              </div>

              {/* Account Information */}
              <div className="md:col-span-2">
                <h3 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b">Account Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex">
                    <span className="font-medium text-gray-600 w-40">User ID:</span>
                    <span className="text-gray-800">{userId._id}</span>
                  </div>
                  
                  <div className="flex">
                    <span className="font-medium text-gray-600 w-40">Role:</span>
                    <span className="text-gray-800 capitalize">{userId.role}</span>
                  </div>
                  
                  <div className="flex">
                    <span className="font-medium text-gray-600 w-40">Status:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      userId.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {userId.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-8 flex space-x-4">
              <Link 
                to={`/admin/students/edit/${id}`} 
                className="bg-primary text-white px-6 py-2 rounded hover:bg-blue-700 transition"
              >
                Edit Student
              </Link>
              
              <button 
                onClick={() => navigate('/admin/students')}
                className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600 transition"
              >
                Back to List
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewStudent;